import React, { useState, useEffect, useCallback } from "react";
import { useStaffMe } from "../context/StaffMeContext";
import CsintAnalyticsPanel from "./CsintAnalyticsPanel";
import { api } from "../api/staff";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  TrendingUp,
  Activity,
  Zap,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ShieldCheck,
  RefreshCw,
  Users,
  Radio,
  Cpu,
  UserCheck,
  MessageSquare,
  Bot,
  Hash,
  Server,
  Image as ImageIcon,
  FileText,
  CornerDownRight,
  Sparkles,
  Mic,
  MicOff,
  Tag,
  Layers,
  Coins,
  DollarSign,
  Wallet,
} from "lucide-react";

const COLORS = ["#3b82f6", "#8b5cf6", "#22c55e", "#f59e0b", "#ef4444", "#06b6d4"];

interface DiscordStatsPayload {
  totalMessages: number;
  messagesLast24h: number;
  totalTargets: number;
  activeTargets: number;
  totalAvatars: number;
  totalUsernames: number;
  totalDisplayNames: number;
  totalBios: number;
  totalGuilds: number;
  totalChannels: number;
  totalAuthors: number;
  totalReplies: number;
  totalVoice?: number;
  voiceLast24h?: number;
  totalMutes?: number;
  mutesLast24h?: number;
  totalStatuses?: number;
  statusesLast24h?: number;
  totalGuildTags?: number;
  guildTagsLast24h?: number;
  messagesByDay?: Array<{ date: string; count: number }>;
  voiceByDay?: Array<{ date: string; count: number }>;
  muteByDay?: Array<{ date: string; count: number }>;
  usernamesByDay?: Array<{ date: string; count: number }>;
  displayNamesByDay?: Array<{ date: string; count: number }>;
  statusByDay?: Array<{ date: string; count: number }>;
  guildTagsByDay?: Array<{ date: string; count: number }>;
  avatarsByDay?: Array<{ date: string; count: number }>;
  biosByDay?: Array<{ date: string; count: number }>;
  combinedByDay?: Array<{
    date: string;
    messages: number;
    voice: number;
    mutes: number;
    usernames: number;
    displayNames: number;
    status: number;
    guildTags: number;
    avatars: number;
  }>;
}

interface GlobalAnalyticsPayload {
  days: any[];
  totalUsers: number;
  totalActiveUsers: number;
  totalOsintResults: number;
  peakHourUtc: number;
  revenue?: {
    totalMinor: number;
    totalFormatted: string;
    invoiceCount: number;
    starsCount: number;
    totalTransactionsCount: number;
    cryptoRevenueFormatted: string;
    starsRevenueFormatted: string;
  };
  userGrowth?: Array<{ month: string; count: number }>;
  serviceBreakdown?: Array<{ name: string; value: number }>;
  loginActivity?: Array<{ date: string; success: number; failed: number }>;
  activeDiscordBots?: number;
  discordStats?: DiscordStatsPayload;
  systemServices?: Array<{ name: string; status: string; uptime: string }>;
  previousTotals?: any;
  connectedUsersCount?: number;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-card border border-border rounded-xl px-3 py-2 shadow-lg text-xs">
      <p className="font-semibold text-foreground mb-1">{label}</p>
      {payload.map((p, idx) => (
        <p key={idx} style={{ color: p.color }} className="flex items-center gap-1.5">
          <span>{p.name}:</span>
          <span className="font-bold">{p.value}</span>
        </p>
      ))}
    </div>
  );
};

function ChartCard({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="bg-card border border-border rounded-2xl p-5 shadow-xs flex flex-col justify-between">
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="text-sm font-semibold text-foreground tracking-tight">{title}</h3>
        {subtitle && <span className="text-[11px] text-muted-foreground">{subtitle}</span>}
      </div>
      <div className="w-full">{children}</div>
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === "operational") return <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />;
  if (status === "degraded") return <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />;
  return <XCircle className="w-3.5 h-3.5 text-rose-500" />;
}

export function Dashboard() {
  const me = useStaffMe();
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<GlobalAnalyticsPayload | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const res = await api<GlobalAnalyticsPayload>("/staff/admin/global-analytics");
      setData(res);
    } catch (err) {
      console.error("Failed to load global analytics", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchAnalytics();
    }, 30000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchAnalytics]);

  // Derived real data
  const userGrowth =
    data?.userGrowth && data.userGrowth.length > 0
      ? data.userGrowth
      : [{ month: "En cours", count: data?.totalUsers ?? 0 }];

  const serviceBreakdown =
    data?.serviceBreakdown && data.serviceBreakdown.length > 0
      ? data.serviceBreakdown.map((s) => ({
          name: s.name.toUpperCase(),
          value: Number(s.value),
        }))
      : [
          { name: "OSINT", value: 1 },
          { name: "DISCORD", value: 1 },
        ];

  const loginActivity =
    data?.loginActivity && data.loginActivity.length > 0
      ? data.loginActivity
      : [{ date: "Aujourd'hui", success: 1, failed: 0 }];

  const systemServices = data?.systemServices || [
    { name: "PostgreSQL Production DB", status: "operational", uptime: "99.99%" },
    { name: "Auth & Zero-Trust Nonce", status: "operational", uptime: "100%" },
    {
      name: "Discord Puppet Workers",
      status: (data?.activeDiscordBots ?? 0) > 0 ? "operational" : "degraded",
      uptime: `${data?.activeDiscordBots ?? 0} actif(s)`,
    },
    { name: "Redis Cache & Pub/Sub", status: "operational", uptime: "99.98%" },
  ];

  const discordStats = data?.discordStats;

  type DiscordMetricType =
    | "messages"
    | "voice"
    | "mute"
    | "usernames"
    | "displayNames"
    | "status"
    | "guildTags"
    | "avatars"
    | "bios"
    | "all";

  const [selectedDiscordMetric, setSelectedDiscordMetric] = useState<DiscordMetricType>("messages");

  interface MetricConfig {
    id: DiscordMetricType;
    label: string;
    unit: string;
    icon: any;
    color: string;
    total: number;
    last24h?: number;
    data: Array<{ date: string; count: number }>;
  }

  const DISCORD_METRICS: MetricConfig[] = [
    {
      id: "messages",
      label: "Messages",
      unit: "Messages indexés",
      icon: MessageSquare,
      color: "#6366f1",
      total: discordStats?.totalMessages ?? 0,
      last24h: discordStats?.messagesLast24h,
      data: discordStats?.messagesByDay ?? [],
    },
    {
      id: "voice",
      label: "Vocaux",
      unit: "Sessions vocales",
      icon: Mic,
      color: "#10b981",
      total: discordStats?.totalVoice ?? 0,
      last24h: discordStats?.voiceLast24h,
      data: discordStats?.voiceByDay ?? [],
    },
    {
      id: "mute",
      label: "Mutes & Deaf",
      unit: "Mutes / Deaf détectés",
      icon: MicOff,
      color: "#f43f5e",
      total: discordStats?.totalMutes ?? 0,
      last24h: discordStats?.mutesLast24h,
      data: discordStats?.muteByDay ?? [],
    },
    {
      id: "usernames",
      label: "Usernames",
      unit: "Usernames indexés",
      icon: Users,
      color: "#06b6d4",
      total: discordStats?.totalUsernames ?? 0,
      data: discordStats?.usernamesByDay ?? [],
    },
    {
      id: "displayNames",
      label: "Noms d'affichage",
      unit: "Global names",
      icon: FileText,
      color: "#a855f7",
      total: discordStats?.totalDisplayNames ?? 0,
      data: discordStats?.displayNamesByDay ?? [],
    },
    {
      id: "status",
      label: "Statuts & Présence",
      unit: "Statuts observés",
      icon: Activity,
      color: "#eab308",
      total: discordStats?.totalStatuses ?? 0,
      last24h: discordStats?.statusesLast24h,
      data: discordStats?.statusByDay ?? [],
    },
    {
      id: "guildTags",
      label: "Guild Tags / Clans",
      unit: "Tags de guilde",
      icon: Tag,
      color: "#ec4899",
      total: discordStats?.totalGuildTags ?? 0,
      last24h: discordStats?.guildTagsLast24h,
      data: discordStats?.guildTagsByDay ?? [],
    },
    {
      id: "avatars",
      label: "Avatars",
      unit: "Avatars historiques",
      icon: ImageIcon,
      color: "#3b82f6",
      total: discordStats?.totalAvatars ?? 0,
      data: discordStats?.avatarsByDay ?? [],
    },
    {
      id: "bios",
      label: "Bios",
      unit: "Bios capturées",
      icon: Sparkles,
      color: "#f97316",
      total: discordStats?.totalBios ?? 0,
      data: discordStats?.biosByDay ?? [],
    },
    {
      id: "all",
      label: "Tout (Vue Globale)",
      unit: "Tous les flux",
      icon: Layers,
      color: "#8b5cf6",
      total: (discordStats?.totalMessages ?? 0) + (discordStats?.totalAvatars ?? 0) + (discordStats?.totalUsernames ?? 0),
      data: [],
    },
  ];

  const currentMetric: MetricConfig = DISCORD_METRICS.find((m) => m.id === selectedDiscordMetric) ?? DISCORD_METRICS[0]!;

  // Dynamic real AI insights
  const dynamicInsights = [
    {
      icon: TrendingUp,
      color: "text-emerald-500",
      text: `${data?.totalUsers ?? 0} utilisateur(s) enregistrés au total dans la base de données.`,
    },
    {
      icon: Zap,
      color: "text-amber-500",
      text: `${discordStats?.totalMessages ?? 0} messages Discord capturés (${discordStats?.messagesLast24h ?? 0} ces dernières 24h).`,
    },
    {
      icon: Activity,
      color: "text-blue-500",
      text: `${discordStats?.totalAuthors ?? 0} auteurs uniques & ${discordStats?.totalGuilds ?? 0} serveurs indexés 24h/24.`,
    },
  ];

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-foreground">
            Bonjour, {me.displayName || me.username || "Staff"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Console de supervision globale & métriques opérationnelles en temps réel.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setLoading(true);
              fetchAnalytics();
            }}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border bg-card border-border text-muted-foreground hover:bg-accent transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Actualiser
          </button>
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-medium border transition-colors ${
              autoRefresh
                ? "bg-primary/10 border-primary text-primary"
                : "bg-card border-border text-muted-foreground hover:bg-accent"
            }`}
          >
            <span
              className={`w-2 h-2 rounded-full ${
                autoRefresh ? "bg-primary animate-pulse" : "bg-muted-foreground"
              }`}
            />
            {autoRefresh ? "Live (30s)" : "Auto Refresh"}
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-card border border-border">
            <span className="flex h-2 w-2 relative">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
            </span>
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              Système Opérationnel • {data?.connectedUsersCount ?? 0} en direct
            </span>
          </div>
        </div>
      </div>

      {/* KPI Cards Grid with Real DB Counts, Revenue & Live Presence */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {/* Card 0: Total Revenue Encaissé */}
        <div className="p-5 rounded-2xl bg-card border border-amber-500/30 shadow-xs flex flex-col justify-between relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-amber-500/5 rounded-full blur-xl pointer-events-none group-hover:bg-amber-500/10 transition-colors" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-500" />
              Argent Encaissé
            </span>
            <div className="p-2 rounded-xl bg-amber-500/10 text-amber-500">
              <Coins className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-amber-500 tracking-tight flex items-baseline gap-2">
              {loading ? "..." : (data?.revenue?.totalFormatted ?? "0.00 €")}
            </div>
            <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-muted-foreground flex-wrap">
              <span>{data?.revenue?.totalTransactionsCount ?? 0} transaction(s)</span>
              {data?.revenue?.starsCount ? (
                <span>• ⭐ {data.revenue.starsCount} Stars</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Card 1: Connected Users Right Now */}
        <div className="p-5 rounded-2xl bg-card border border-emerald-500/20 shadow-xs flex flex-col justify-between relative overflow-hidden group">
          <div className="absolute top-0 right-0 w-24 h-24 bg-emerald-500/5 rounded-full blur-xl pointer-events-none group-hover:bg-emerald-500/10 transition-colors" />
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              En Ligne
            </span>
            <div className="p-2 rounded-xl bg-emerald-500/10 text-emerald-500">
              <Radio className="w-4 h-4 animate-pulse" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-emerald-500 tracking-tight flex items-baseline gap-2">
              {loading ? "..." : (data?.connectedUsersCount ?? 0).toLocaleString()}
              <span className="text-[11px] font-normal text-muted-foreground">actif(s)</span>
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Connectés en temps réel</p>
          </div>
        </div>

        {/* Card 2: Total Users */}
        <div className="p-5 rounded-2xl bg-card border border-border shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Total Utilisateurs
            </span>
            <div className="p-2 rounded-xl bg-primary/10 text-primary">
              <Users className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (data?.totalUsers ?? 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Comptes enregistrés en base</p>
          </div>
        </div>

        {/* Card 3: Active Users */}
        <div className="p-5 rounded-2xl bg-card border border-border shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Utilisateurs Actifs
            </span>
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-500">
              <UserCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (data?.totalActiveUsers ?? 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Activité de recherche enregistrée</p>
          </div>
        </div>

        {/* Card 4: Discord Messages Scraped */}
        <div className="p-5 rounded-2xl bg-card border border-border shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Messages Scrappés
            </span>
            <div className="p-2 rounded-xl bg-indigo-500/10 text-indigo-500">
              <MessageSquare className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (discordStats?.totalMessages ?? 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              +{discordStats?.messagesLast24h ?? 0} dernières 24h
            </p>
          </div>
        </div>

        {/* Card 5: Discord Bots */}
        <div className="p-5 rounded-2xl bg-card border border-border shadow-xs flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Bots Discord Actifs
            </span>
            <div className="p-2 rounded-xl bg-blue-500/10 text-blue-500">
              <Radio className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-4">
            <div className="text-2xl font-bold text-foreground tracking-tight">
              {loading ? "..." : data?.activeDiscordBots ?? 0}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">Puppets OSINT connectés</p>
          </div>
        </div>
      </div>

      {/* ── Discord Scraping Telemetry Section (Real Scrape Stats) ── */}
      <div className="bg-card border border-border rounded-2xl p-6 shadow-xs space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-border/60">
          <div className="flex items-start gap-3.5">
            <div className="p-2.5 rounded-xl bg-indigo-500/10 text-indigo-500 shrink-0 mt-0.5">
              <Radio className="w-5 h-5 animate-pulse" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-foreground tracking-tight">
                  Surveillance & Scraping Discord
                </h2>
                <span className="text-[10px] font-semibold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 inline-block animate-ping" />
                  Capture Continue 24h/24
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Statistiques complètes du scraping Discord : messages, cibles surveillées, profils et relations.
              </p>
            </div>
          </div>
        </div>

        {/* Discord KPIs Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="p-3.5 rounded-xl bg-background border border-border/70 flex flex-col justify-between">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] font-medium">Messages 24h</span>
              <MessageSquare className="w-3.5 h-3.5 text-indigo-500" />
            </div>
            <div className="mt-2 text-xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (discordStats?.messagesLast24h ?? 0).toLocaleString()}
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/70 flex flex-col justify-between">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] font-medium">Cibles Actives</span>
              <Users className="w-3.5 h-3.5 text-emerald-500" />
            </div>
            <div className="mt-2 text-xl font-bold text-foreground tracking-tight">
              {loading
                ? "..."
                : `${discordStats?.activeTargets ?? 0} / ${discordStats?.totalTargets ?? 0}`}
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/70 flex flex-col justify-between">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] font-medium">Auteurs Uniques</span>
              <UserCheck className="w-3.5 h-3.5 text-blue-500" />
            </div>
            <div className="mt-2 text-xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (discordStats?.totalAuthors ?? 0).toLocaleString()}
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/70 flex flex-col justify-between">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] font-medium">Serveurs Suivis</span>
              <Server className="w-3.5 h-3.5 text-purple-500" />
            </div>
            <div className="mt-2 text-xl font-bold text-foreground tracking-tight">
              {loading ? "..." : discordStats?.totalGuilds ?? 0}
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/70 flex flex-col justify-between">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] font-medium">Salons Monitorés</span>
              <Hash className="w-3.5 h-3.5 text-amber-500" />
            </div>
            <div className="mt-2 text-xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (discordStats?.totalChannels ?? 0).toLocaleString()}
            </div>
          </div>

          <div className="p-3.5 rounded-xl bg-background border border-border/70 flex flex-col justify-between">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="text-[11px] font-medium">Réponses Capturées</span>
              <CornerDownRight className="w-3.5 h-3.5 text-cyan-500" />
            </div>
            <div className="mt-2 text-xl font-bold text-foreground tracking-tight">
              {loading ? "..." : (discordStats?.totalReplies ?? 0).toLocaleString()}
            </div>
          </div>
        </div>

        {/* Changes & Scraped metadata history row 1 */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-4 h-4 text-blue-500" />
              <span className="text-xs text-muted-foreground">Avatars historiques</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalAvatars ?? 0).toLocaleString()}
            </span>
          </div>

          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">Usernames indexés</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalUsernames ?? 0).toLocaleString()}
            </span>
          </div>

          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-purple-500" />
              <span className="text-xs text-muted-foreground">Noms d'affichage</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalDisplayNames ?? 0).toLocaleString()}
            </span>
          </div>

          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-amber-500" />
              <span className="text-xs text-muted-foreground">Bios capturées</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalBios ?? 0).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Telemetry metadata history row 2: Vocaux, Mute, Status, Guild Tags */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Mic className="w-4 h-4 text-emerald-500" />
              <span className="text-xs text-muted-foreground">Sessions Vocales</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalVoice ?? 0).toLocaleString()}
            </span>
          </div>

          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MicOff className="w-4 h-4 text-rose-500" />
              <span className="text-xs text-muted-foreground">Mutes & Deaf</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalMutes ?? 0).toLocaleString()}
            </span>
          </div>

          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-yellow-500" />
              <span className="text-xs text-muted-foreground">Statuts & Présence</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalStatuses ?? 0).toLocaleString()}
            </span>
          </div>

          <div className="p-3 rounded-xl bg-background border border-border/50 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Tag className="w-4 h-4 text-pink-500" />
              <span className="text-xs text-muted-foreground">Guild Tags / Clans</span>
            </div>
            <span className="text-sm font-bold text-foreground">
              {loading ? "..." : (discordStats?.totalGuildTags ?? 0).toLocaleString()}
            </span>
          </div>
        </div>

        {/* Discord Scraping Activity Chart with Multi-Stream Switcher */}
        <div className="pt-3 border-t border-border/50 space-y-3">
          {/* Top Bar: Title & Metric Pills */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-foreground">
                  Progression Quotidienne : {currentMetric.label} (7 derniers jours)
                </span>
                <span className="text-[11px] text-muted-foreground font-mono">
                  {currentMetric.unit}
                </span>
                {currentMetric.last24h !== undefined && currentMetric.last24h > 0 && (
                  <span className="text-[10px] font-semibold text-emerald-500 bg-emerald-500/10 px-2 py-0.5 rounded-full">
                    +{currentMetric.last24h.toLocaleString()} dernières 24h
                  </span>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Cliquez sur un flux pour inspecter sa courbe d'évolution journalière.
              </p>
            </div>

            {/* Metric Selector Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 max-w-full scrollbar-none">
              {DISCORD_METRICS.map((metric) => {
                const Icon = metric.icon;
                const isActive = selectedDiscordMetric === metric.id;
                return (
                  <button
                    key={metric.id}
                    type="button"
                    onClick={() => setSelectedDiscordMetric(metric.id)}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 whitespace-nowrap transition-all border shrink-0 ${
                      isActive
                        ? "bg-primary text-primary-foreground border-primary shadow-xs"
                        : "bg-muted/40 hover:bg-muted text-muted-foreground hover:text-foreground border-border/40"
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: isActive ? "currentColor" : metric.color }} />
                    <span>{metric.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Chart Display Area */}
          <div className="h-[180px] w-full pt-1">
            <ResponsiveContainer width="100%" height="100%">
              {selectedDiscordMetric === "all" ? (
                <LineChart
                  data={discordStats?.combinedByDay && discordStats.combinedByDay.length > 0 ? discordStats.combinedByDay : []}
                  margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "6px" }} />
                  <Line type="monotone" dataKey="messages" stroke="#6366f1" strokeWidth={2} name="Messages" dot={false} />
                  <Line type="monotone" dataKey="voice" stroke="#10b981" strokeWidth={2} name="Vocaux" dot={false} />
                  <Line type="monotone" dataKey="mutes" stroke="#f43f5e" strokeWidth={2} name="Mutes" dot={false} />
                  <Line type="monotone" dataKey="usernames" stroke="#06b6d4" strokeWidth={2} name="Usernames" dot={false} />
                  <Line type="monotone" dataKey="displayNames" stroke="#a855f7" strokeWidth={2} name="Noms d'affichage" dot={false} />
                  <Line type="monotone" dataKey="status" stroke="#eab308" strokeWidth={2} name="Statuts" dot={false} />
                  <Line type="monotone" dataKey="guildTags" stroke="#ec4899" strokeWidth={2} name="Guild Tags" dot={false} />
                  <Line type="monotone" dataKey="avatars" stroke="#3b82f6" strokeWidth={2} name="Avatars" dot={false} />
                </LineChart>
              ) : (
                <AreaChart
                  data={currentMetric.data && currentMetric.data.length > 0 ? currentMetric.data : []}
                  margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                >
                  <defs>
                    <linearGradient id="metricScrapeGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={currentMetric.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={currentMetric.color} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke={currentMetric.color}
                    strokeWidth={2}
                    fill="url(#metricScrapeGrad)"
                    name={currentMetric.label}
                    dot={false}
                  />
                </AreaChart>
              )}
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* ── Main RBACStack Analytics Row: 3 Columns ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left Column: User Growth & Traffic Sources */}
        <div className="flex flex-col gap-5">
          <ChartCard title="Croissance Utilisateurs (Réel)" subtitle="Inscriptions mensuelles réelles">
            <div className="h-[180px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={userGrowth} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="userGrowthGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="count"
                    stroke="#3b82f6"
                    strokeWidth={2}
                    fill="url(#userGrowthGrad)"
                    name="Inscrits"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Répartition des Services (Réel)" subtitle="Usage consommé">
            <div className="h-[180px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={serviceBreakdown}
                    cx="50%"
                    cy="45%"
                    innerRadius={45}
                    outerRadius={65}
                    dataKey="value"
                    paddingAngle={4}
                  >
                    {serviceBreakdown.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                  <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* Center Column: Daily Activity & Login Activity */}
        <div className="flex flex-col gap-5">
          <ChartCard title="Volume d'Activité Récent" subtitle="Recherches par jour">
            <div className="h-[180px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.days ? data.days.slice(-7) : []}
                  margin={{ top: 5, right: 10, left: -20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="total" fill="#3b82f6" name="Requêtes" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="credits" fill="#8b5cf6" name="Crédits" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>

          <ChartCard title="Connexions & Authentifications" subtitle="Tentatives réelles (14 jours)">
            <div className="h-[180px] w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={loginActivity} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 10 }} stroke="#888888" axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip />} />
                  <Line type="monotone" dataKey="success" stroke="#22c55e" strokeWidth={2} dot={false} name="Succès" />
                  <Line type="monotone" dataKey="failed" stroke="#ef4444" strokeWidth={2} dot={false} name="Échecs" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>

        {/* Right Column: Dynamic Real AI Insights & System Status */}
        <div className="flex flex-col gap-5">
          {/* AI Insights Card */}
          <div className="bg-gradient-to-br from-primary/10 via-purple-500/10 to-transparent border border-primary/20 rounded-2xl p-5 shadow-xs">
            <div className="flex items-center justify-between mb-3.5">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">AI Insights</span>
              </div>
              <span className="text-[10px] bg-primary/15 text-primary px-2 py-0.5 rounded-full font-medium">
                Télémétrie Live
              </span>
            </div>
            <div className="space-y-2.5">
              {dynamicInsights.map(({ icon: Icon, color, text }, i) => (
                <div
                  key={i}
                  className="flex items-start gap-2.5 bg-background/60 backdrop-blur-xs rounded-xl p-2.5 border border-border/50"
                >
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
                  <p className="text-xs text-muted-foreground leading-relaxed">{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* System Services Status */}
          <div className="bg-card border border-border rounded-2xl p-5 shadow-xs flex flex-col justify-between">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-foreground">Statut des Composants</p>
              <span className="text-[10px] bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full font-medium flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block" />
                Services opérationnels
              </span>
            </div>
            <div className="space-y-2.5">
              {systemServices.map((s) => (
                <div
                  key={s.name}
                  className="flex items-center justify-between gap-2 py-1 border-b border-border/40 last:border-0"
                >
                  <div className="flex items-center gap-2">
                    <StatusIcon status={s.status} />
                    <span className="text-xs font-medium text-foreground">{s.name}</span>
                  </div>
                  <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
                    {s.uptime}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* CSINT Analytics Corpus Panel */}
      {(me.role === "developer" || me.role === "admin") && (
        <div className="pt-2">
          <CsintAnalyticsPanel />
        </div>
      )}
    </div>
  );
}
